import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { MushakFormsService } from '../../services/mushakForms.service';
import { AuthService } from '../../services/auth.service';
import { Mushak610, Mushak610Row, MushakFormSettings } from '../../interfaces/indirectTax.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { formatMoney } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { saveBlob } from '../../utils/saveBlob';

const pad = (n: number) => String(n).padStart(2, '0');

/** First and last day of the previous calendar month, as local yyyy-MM-dd. */
export const lastMonthRange = (now: Date = new Date()): { from: string; to: string } => {
  const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const last = new Date(now.getFullYear(), now.getMonth(), 0);
  const iso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  return { from: iso(first), to: iso(last) };
};

const DOC_TYPE_LABEL: Record<string, string> = {
  SALES_INVOICE: 'Invoice', SUPPLIER_BILL: 'Bill', SALES_CREDIT_NOTE: 'Credit note', PURCHASE_DEBIT_NOTE: 'Debit note',
};

const RowsTable: React.FC<{ title: string; rows: Mushak610Row[]; total?: number; testId: string }> = ({ title, rows, total, testId }) => (
  <Paper sx={{ p: 2, mb: 2, overflowX: 'auto' }}>
    <Typography variant="subtitle1">{title}</Typography>
    <Table data-help-section="tax.mushak610.table" size="small" sx={{ mt: 1 }} data-testid={testId}>
      <TableHead>
        <TableRow>
          <TableCell data-help="tax.mushak610.col.type">Type</TableCell><TableCell data-help="tax.mushak610.col.document">Document</TableCell><TableCell data-help="tax.mushak610.col.date">Date</TableCell><TableCell data-help="tax.mushak610.col.party">Party</TableCell><TableCell data-help="tax.mushak610.col.bin">BIN</TableCell>
          <TableCell data-help="tax.mushak610.col.address">Address</TableCell><TableCell data-help="tax.mushak610.col.value" align="right">Value</TableCell><TableCell data-help="tax.mushak610.col.vat" align="right">VAT</TableCell>
          <TableCell data-help="tax.mushak610.col.total" align="right">Total</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.length === 0 && <TableRow><TableCell colSpan={9}>No documents above the threshold.</TableCell></TableRow>}
        {rows.map((r, i) => (
          <TableRow key={`${r.documentType}-${r.documentNumber}-${r.date}-${i}`}>
            <TableCell>{DOC_TYPE_LABEL[r.documentType] ?? r.documentType}</TableCell>
            <TableCell>{r.documentNumber}{r.note ? ` (${r.note})` : ''}</TableCell>
            <TableCell>{formatDate(r.date)}</TableCell>
            <TableCell>{r.partyName}</TableCell>
            <TableCell>{r.partyBin}</TableCell>
            <TableCell>{r.partyAddress}</TableCell>
            <TableCell align="right">{formatMoney(r.value)}</TableCell>
            <TableCell align="right">{formatMoney(r.vat)}</TableCell>
            <TableCell align="right">{formatMoney(r.total)}</TableCell>
          </TableRow>
        ))}
        {total !== undefined && (
          <TableRow>
            <TableCell colSpan={8}><strong>Total</strong></TableCell>
            <TableCell align="right" data-testid={`${testId}-total`}><strong>{formatMoney(total)}</strong></TableCell>
          </TableRow>
        )}
      </TableBody>
    </Table>
  </Paper>
);

const Mushak610Page: React.FC = () => {
  const toast = useToast();
  const institutionId = AuthService.getSelectedInstitution() as string;
  const canManage = usePermission(PERMISSIONS.INDIRECT_TAX_MANAGE);
  const initial = lastMonthRange();

  const [from, setFrom] = useState(initial.from);
  const [to, setTo] = useState(initial.to);
  const [data, setData] = useState<Mushak610 | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [settings, setSettings] = useState<MushakFormSettings | null>(null);
  const [draft, setDraft] = useState<Omit<MushakFormSettings, 'institutionId' | 'loaded'> | null>(null);

  const load = useCallback(async () => {
    if (!from || !to) return;
    try {
      setLoading(true);
      setData(await MushakFormsService.get610(institutionId, from, to));
    } catch (e) {
      setData(null);
      toast.showApiError(e, 'Failed to load Mushak 6.10');
    } finally {
      setLoading(false);
    }
  }, [institutionId, from, to]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const loadSettings = useCallback(async () => {
    try {
      const s = await MushakFormsService.getFormSettings(institutionId);
      setSettings(s);
      setDraft({ thresholdAmount: s.thresholdAmount, vdsCustomerNoteRef: s.vdsCustomerNoteRef, vdsSupplierNoteRef: s.vdsSupplierNoteRef,
        form43Footer: s.form43Footer, verifyNote: s.verifyNote });
    } catch (e) {
      toast.showApiError(e, 'Failed to load form settings');
    }
  }, [institutionId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const doExport = async (format: 'pdf' | 'xlsx') => {
    try {
      setExporting(true);
      saveBlob(await MushakFormsService.download610(institutionId, from, to, format), `mushak-6-10-${from}-to-${to}.${format}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to export Mushak 6.10');
    } finally {
      setExporting(false);
    }
  };

  const loadDefaults = async () => {
    try {
      await MushakFormsService.loadFormDefaults(institutionId);
      toast.showSuccess('Form defaults loaded - review and edit them (marked verify)');
      await loadSettings();
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to load defaults');
    }
  };

  const saveSettings = async () => {
    if (!draft) return;
    try {
      await MushakFormsService.saveFormSettings(institutionId, draft);
      toast.showSuccess('Form settings saved');
      await loadSettings();
      await load();
    } catch (e) {
      toast.showApiError(e, 'Failed to save form settings');
    }
  };

  const setField = (k: keyof NonNullable<typeof draft>, v: string) =>
    setDraft(d => (d ? { ...d, [k]: k === 'thresholdAmount' ? Number(v) : v } : d));

  return (
    <Box data-help-page="tax.mushak610.page" sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 1 }}>Mushak 6.10 - Purchases and Sales above the Threshold</Typography>
      <Alert severity="warning" sx={{ mb: 2 }}>
        Working statement from the tax ledger - verify the threshold and the form layout against the current NBR form
        with your VAT consultant, and file it with the monthly return.
      </Alert>

      <Box data-help-section="tax.mushak610.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, alignItems: 'center' }}>
        <Box sx={{ flex: '1 1 160px', maxWidth: 220 }}>
          <TextField data-help="tax.mushak610.from" fullWidth size="small" type="date" label="From" value={from} InputLabelProps={{ shrink: true }}
            onChange={e => setFrom(e.target.value)} />
        </Box>
        <Box sx={{ flex: '1 1 160px', maxWidth: 220 }}>
          <TextField data-help="tax.mushak610.to" fullWidth size="small" type="date" label="To" value={to} InputLabelProps={{ shrink: true }}
            onChange={e => setTo(e.target.value)} />
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button data-help="tax.mushak610.pdf" variant="outlined" onClick={() => doExport('pdf')} disabled={exporting || loading || !data}>Download PDF</Button>
          <Button data-help="tax.mushak610.xlsx" variant="outlined" onClick={() => doExport('xlsx')} disabled={exporting || loading || !data}>Export XLSX</Button>
        </Box>
      </Box>

      {loading && <CircularProgress />}
      {!loading && data && (
        <>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {data.institutionName} | BIN {data.bin || '(not set)'} | {formatDate(data.from)} to {formatDate(data.to)} |
            {' '}threshold BDT <span data-testid="mushak610-threshold">{formatMoney(data.threshold)}</span>
            {!data.settingsLoaded && ' (built-in default - load the form defaults to edit it)'}
          </Typography>
          <RowsTable title="Part 1 - Purchases" rows={data.purchases} total={data.purchaseTotal} testId="mushak610-purchases" />
          <RowsTable title="Part 2 - Sales" rows={data.sales} total={data.salesTotal} testId="mushak610-sales" />
          {data.notes.length > 0 && <RowsTable title="Credit and debit notes" rows={data.notes} testId="mushak610-notes" />}
          {data.cancelled.length > 0 && <RowsTable title="Cancelled (voided or reversed; not in the totals)" rows={data.cancelled} testId="mushak610-cancelled" />}
          {data.remarks.map(n => <Typography key={n} variant="caption" color="text.secondary" display="block">{n}</Typography>)}
        </>
      )}

      <Paper data-help-section="tax.mushak610.settings" sx={{ p: 2, mt: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Mushak form settings (marked verify)</Typography>
        {settings && !settings.loaded && (
          <Alert severity="info" sx={{ mb: 2 }}>
            No settings yet. {canManage ? 'Load the defaults, then edit the values to match your VAT consultant\'s advice.' : 'Ask a tax manager to load the defaults.'}
          </Alert>
        )}
        {canManage && settings && !settings.loaded && (
          <Button data-help="tax.mushak610.loadDefaults" variant="contained" onClick={loadDefaults} sx={{ mb: 2 }}>Load form defaults</Button>
        )}
        {draft && settings?.loaded && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ flex: '1 1 200px', maxWidth: 260 }}>
              <TextField data-help="tax.mushak610.threshold" fullWidth size="small" type="number" label="6.10 threshold (BDT)" disabled={!canManage}
                value={draft.thresholdAmount} onChange={e => setField('thresholdAmount', e.target.value)} />
            </Box>
            <Box sx={{ flex: '1 1 300px', maxWidth: 480 }}>
              <TextField data-help="tax.mushak610.noteCustomers" fullWidth size="small" label="9.1 note: VDS deducted by customers" disabled={!canManage}
                value={draft.vdsCustomerNoteRef ?? ''} onChange={e => setField('vdsCustomerNoteRef', e.target.value)} />
            </Box>
            <Box sx={{ flex: '1 1 300px', maxWidth: 480 }}>
              <TextField data-help="tax.mushak610.noteSuppliers" fullWidth size="small" label="9.1 note: VDS we deducted" disabled={!canManage}
                value={draft.vdsSupplierNoteRef ?? ''} onChange={e => setField('vdsSupplierNoteRef', e.target.value)} />
            </Box>
            <Box sx={{ flex: '1 1 300px', maxWidth: 480 }}>
              <TextField data-help="tax.mushak610.footer" fullWidth size="small" label="4.3 footer" disabled={!canManage}
                value={draft.form43Footer ?? ''} onChange={e => setField('form43Footer', e.target.value)} />
            </Box>
            <Box sx={{ flex: '1 1 300px', maxWidth: 480 }}>
              <TextField data-help="tax.mushak610.verifyNote" fullWidth size="small" label="Verify note" disabled={!canManage}
                value={draft.verifyNote ?? ''} onChange={e => setField('verifyNote', e.target.value)} />
            </Box>
            {canManage && <Button data-help="tax.mushak610.saveSettings" variant="outlined" onClick={saveSettings}>Save settings</Button>}
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default Mushak610Page;
